import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { jwtDecode, JwtPayload } from "jwt-decode";
import axios from "axios";

import {
  AuthUser,
  JWTAuthActionsProps,
  JWTAuthContextProps,
  JWTAuthProviderProps,
  Token,
} from "../../../types/Auth";
import { reconnectSocket, disconnectSocket } from "../../SocketClient";

const JWTAuthContext = createContext<JWTAuthContextProps>({
  user: null,
  isAuthenticated: false,
  isLoading: false,
});

const JWTAuthActionsContext = createContext<JWTAuthActionsProps>({
  signInUser: () => {},
  logout: () => {},
});

export const useJWTAuth = () => useContext(JWTAuthContext);
export const useJWTAuthActions = () => useContext(JWTAuthActionsContext);

const JWTAuthProvider: React.FC<JWTAuthProviderProps> = ({ children }) => {
  const [authData, setJWTAuthData] = useState<JWTAuthContextProps>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Get access token from local storage
  const getAccessToken = useCallback(() => {
    return localStorage.getItem("accessToken");
  }, []);

  // Check if the access token is valid
  const isTokenValid = useCallback((accessToken: string | null) => {
    if (accessToken) {
      try {
        const decoded = jwtDecode<JwtPayload>(accessToken);
        const currentTime = Date.now() / 1000;
        return decoded?.exp && decoded.exp > currentTime;
      } catch (error) {
        return false;
      }
    }

    return false;
  }, []);

  // Check if the access token exist and is valid on mount
  useEffect(() => {
    const attemptAutoLogin = async () => {
      const accessToken: string | null = getAccessToken();
      if (isTokenValid(accessToken)) {
        setJWTAuthData({
          user: null,
          isAuthenticated: true,
          isLoading: false,
        });
      } else {
        const newToken = await decodeRefresh();

        if (newToken?.success) {
          const tokens = newToken.tokens;
          saveTokenInLocalStorage(tokens);
          setJWTAuthData({
            user: null,
            isAuthenticated: true,
            isLoading: false,
          });
        } else {
          setJWTAuthData({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      }
    };

    attemptAutoLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTokenValid, authData.isAuthenticated]);

  const signInUser = async (data: AuthUser) => {
    saveTokenInLocalStorage(data.tokens);
    reconnectSocket();
    setJWTAuthData({
      user: data.user,
      isAuthenticated: true,
      isLoading: false,
    });
  };

  const logout = async () => {
    disconnectSocket();
    removeTokenFromLocalStorage();
    setJWTAuthData({
      user: null,
      isLoading: false,
      isAuthenticated: false,
    });
  };

  function saveTokenInLocalStorage(tokens: Token) {
    const refreshToken = localStorage.getItem("refreshToken");
    localStorage.setItem("accessToken", tokens.accessToken);
    if (!refreshToken) {
      localStorage.setItem("refreshToken", tokens.refreshToken);
    }
  }

  const removeTokenFromLocalStorage = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
  };

  const decodeRefresh = async () => {
    const now = Date.now();
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      removeTokenFromLocalStorage();
      setJWTAuthData({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
      return;
    }
    const decodedRefresh = jwtDecode<JwtPayload>(refreshToken);
    if (decodedRefresh && typeof decodedRefresh.exp !== "undefined") {
      if (decodedRefresh && decodedRefresh.exp * 1000 < now) {
        removeTokenFromLocalStorage();
        setJWTAuthData({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
        return;
      }
    }
    const newToken = await renewToken(refreshToken);
    return newToken;
  };

  async function renewToken(refreshToken: string) {
    const res = await axios.post(
      `${process.env.REACT_APP_REST_ENDPOINT}/renewToken`,
      { token: refreshToken }
    );

    if (res.status === 403) {
      removeTokenFromLocalStorage();
      setJWTAuthData({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
      return;
    }

    return res.data;
  }

  return (
    <JWTAuthContext.Provider
      value={{
        ...authData,
      }}
    >
      <JWTAuthActionsContext.Provider
        value={{
          signInUser,
          logout,
        }}
      >
        {children}
      </JWTAuthActionsContext.Provider>
    </JWTAuthContext.Provider>
  );
};
export default JWTAuthProvider;
